/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { sortBy } from 'lodash'

import type { SliderInstanceType, SliderNodeType, SliderType } from '@modules/ui-types'

const POSITIONS = 4
const LETTERS = `abcdefghijklmnopqrstuvwxyz`.split(``)

/* Node types without explicit weighting are ordered alphabetically by the first
 * four letters (a compromise between being useful and not blowing the resulting
 * numeric score out to ridiculous proportions).
 *
 * Each position needs to completely outweigh the next one down so that you get:
 *
 * - a
 * - bzzz
 * - c
 *
 * With 4 positions factored in, maximum return value for `zzzz` is 531440.
 */
function getExtraSliderNodeTypeWeight(sliderNodeType: SliderNodeType): number {
  return sliderNodeType
    .toLowerCase() // sanity
    .split(``)
    .slice(0, POSITIONS)
    .reduce((acc: number, letter: string, i: number) => {
      const positionWeight = Math.pow(27, POSITIONS - i - 1) // for 4 positions: 19683, 729, 27, 1
      const letterWeight = LETTERS.indexOf(letter) + 1 // 1-26, or 0 if non-alpha
      return acc + positionWeight * letterWeight
    }, 0)
}

/*
 * Returns a ordering score/weight for a slider instance type and, optionally,
 * node type.
 */
export function getSliderWeight(
  sliderInstanceType?: SliderInstanceType,
  sliderNodeType?: SliderNodeType,
): number {
  /* eslint-disable default-case */
  let weight = 0

  switch (sliderInstanceType) {
    case `elasticsearch`:
      switch (sliderNodeType) {
        case `data`:
          break
        case `data_content`:
          weight += 1
          break
        case `data_hot`:
          weight += 2
          break
        case `data_warm`:
          weight += 3
          break
        case `data_cold`:
          weight += 4
          break
        case `data_frozen`:
          weight += 5
          break
        case `master`:
          weight += 6
          break
        case `ingest`:
          weight += 7
          break
        case `ml`:
          weight += 200
          break
      }

      break
    case `kibana`:
      weight += 100
      break
    case `apm`:
      weight += 300
      break
    case `integrations_server`:
      weight += 350
      break
    case `appsearch`:
      weight += 400

      switch (sliderNodeType) {
        case `appserver`:
          break
        case `worker`:
          weight += 1
          break
      }

      break
    case `enterprise_search`:
      weight += 500

      switch (sliderNodeType) {
        case `appserver`:
          break
        case `connector`:
          weight += 1
          break
        case `worker`:
          weight += 2
          break
      }

      break
    default:
      weight += sliderNodeType ? getExtraSliderNodeTypeWeight(sliderNodeType) : 0
  }

  return weight
}

export function sortSliderTypes(sliderTypes: SliderType[]) {
  return sortBy(sliderTypes, ({ sliderInstanceType, sliderNodeType }) =>
    getSliderWeight(sliderInstanceType, sliderNodeType),
  )
}

// the below is deprecated, to be removed when lib/deployments/architecture is reworked to be SliderType-aware

type NodeType = SliderInstanceType | SliderNodeType

// Interleaves ES node types with Slider types for our ideal display order
const sortedNodeTypes: NodeType[] = [
  `hot`,
  `warm`,
  `cold`,
  `frozen`,
  `data`, // Keep `data` listed as failsafe
  `tiebreaker`,
  `master`,
  `ingest`,
  `kibana`,
  `ml`,
  `apm`,
  `integrations_server`,
  `appsearch`,
  `enterprise_search`,
]

export const sortNodeTypes = (a: NodeType, b: NodeType) =>
  sortedNodeTypes.indexOf(a) - sortedNodeTypes.indexOf(b)
